/**
 * Status GraphQL Service
 * Consumes status-related GraphQL queries from resources/core
 */

import { graphqlClient } from '@/services/core/graphqlClient';
import { STATUS_QUERIES } from '@/services/resources';

export interface StatusOption {
  id: number;
  status: string;
}

export interface StatusUpdates {
  ccm_status_updates?: any[];
  rpm_status_updates?: any[];
  patient_clinic_timezone?: string;
}

// Role to query mapping for CCM status options
const CCM_STATUS_ROLE_MAPPING = {
  'EA': 'GET_CCM_STATUS_OPTIONS_VPE',
  'ES': 'GET_CCM_STATUS_OPTIONS_VPE',
  'CA': 'GET_CCM_STATUS_OPTIONS_CS',
  'CS': 'GET_CCM_STATUS_OPTIONS_CS',
  'PA': 'GET_CCM_STATUS_OPTIONS_GLOBAL',
  'PE': 'GET_CCM_STATUS_OPTIONS_GLOBAL',
  'GLOBAL': 'GET_CCM_STATUS_OPTIONS_GLOBAL'
} as const;

// Role to query mapping for RPM status options
const RPM_STATUS_ROLE_MAPPING = {
  'EA': 'GET_RPM_STATUS_OPTIONS',        // EA uses Global query
  'ES': 'GET_RPM_STATUS_OPTIONS',        // ES uses Global query
  'CA': 'GET_RPM_STATUS_OPTIONS_CS',     // CA uses CS query
  'CS': 'GET_RPM_STATUS_OPTIONS_CS',     // CS uses CS query
  'PA': 'GET_RPM_STATUS_OPTIONS',        // PA uses Global query
  'PE': 'GET_RPM_STATUS_OPTIONS',        // PE uses Global query
  'GLOBAL': 'GET_RPM_STATUS_OPTIONS'     // Global search uses Global query
} as const;

export type UserRole = keyof typeof CCM_STATUS_ROLE_MAPPING;

export class StatusGraphqlService {
  /**
   * Get CCM status options for global use
   */
  async getCCMStatusOptionsGlobal(hasuraToken: string) {
    return await graphqlClient.query<{ pep_v2_status_mapping_ccm: StatusOption[] }>(
      STATUS_QUERIES.GET_CCM_STATUS_OPTIONS_GLOBAL,
      {},
      hasuraToken
    );
  }

  /**
   * Get CCM status options for Care Specialists
   */
  async getCCMStatusOptionsCS(hasuraToken: string) {
    return await graphqlClient.query<{ pep_v2_status_mapping_ccm: StatusOption[] }>(
      STATUS_QUERIES.GET_CCM_STATUS_OPTIONS_CS,
      {},
      hasuraToken
    );
  }

  /**
   * Get CCM status options for Patient Enrollment Specialists
   */
  async getCCMStatusOptionsPES(hasuraToken: string) {
    return await graphqlClient.query<{ pep_v2_status_mapping_ccm: StatusOption[] }>(
      STATUS_QUERIES.GET_CCM_STATUS_OPTIONS_PES,
      {},
      hasuraToken
    );
  }

  /**
   * Get CCM status options for Virtual Patient Enrollers (VPE)
   */
  async getCCMStatusOptionsVPE(hasuraToken: string) {
    return await graphqlClient.query<{ pep_v2_status_mapping_ccm: StatusOption[] }>(
      STATUS_QUERIES.GET_CCM_STATUS_OPTIONS_VPE,
      {},
      hasuraToken
    );
  }

  /**
   * Get CCM status options for any user role using role-based mapping
   */
  async getCCMStatusOptionsByRole(hasuraToken: string, role: UserRole) {
    const queryKey = CCM_STATUS_ROLE_MAPPING[role];
    return await graphqlClient.query<{ pep_v2_status_mapping_ccm: StatusOption[] }>(
      STATUS_QUERIES[queryKey],
      {},
      hasuraToken
    );
  }

  /**
   * Get RPM status options for global use
   */
  async getRPMStatusOptions(hasuraToken: string) {
    return await graphqlClient.query<{ pep_v2_status_mapping_rpm: StatusOption[] }>(
      STATUS_QUERIES.GET_RPM_STATUS_OPTIONS,
      {},
      hasuraToken
    );
  }

  /**
   * Get RPM status options for Care Specialists
   */
  async getRPMStatusOptionsCS(hasuraToken: string) {
    return await graphqlClient.query<{ pep_v2_status_mapping_rpm: StatusOption[] }>(
      STATUS_QUERIES.GET_RPM_STATUS_OPTIONS_CS,
      {},
      hasuraToken
    );
  }

  /**
   * Get RPM status options for Patient Enrollment Specialists
   */
  async getRPMStatusOptionsPES(hasuraToken: string) {
    return await graphqlClient.query<{ pep_v2_status_mapping_rpm: StatusOption[] }>(
      STATUS_QUERIES.GET_RPM_STATUS_OPTIONS_PES,
      {},
      hasuraToken
    );
  }

  /**
   * Get RPM status options for any user role using role-based mapping
   */
  async getRPMStatusOptionsByRole(hasuraToken: string, role: UserRole) {
    const queryKey = RPM_STATUS_ROLE_MAPPING[role];
    return await graphqlClient.query<{ pep_v2_status_mapping_rpm: StatusOption[] }>(
      STATUS_QUERIES[queryKey],
      {},
      hasuraToken
    );
  }

  /**
   * Get CCM status updates for a patient
   */
  async getCCMStatusUpdates(hasuraToken: string, patientId: number) {
    return await graphqlClient.query<{ pep_vw_consolidated_data: StatusUpdates[] }>(
      STATUS_QUERIES.GET_CCM_STATUS_UPDATES,
      { patientId },
      hasuraToken
    );
  }

  /**
   * Get RPM status updates for a patient
   */
  async getRPMStatusUpdates(hasuraToken: string, patientId: number) {
    return await graphqlClient.query<{ pep_vw_consolidated_data: StatusUpdates[] }>(
      STATUS_QUERIES.GET_RPM_STATUS_UPDATES,
      { patientId },
      hasuraToken
    );
  }
}

// Export singleton instance
export const statusGraphqlService = new StatusGraphqlService();
export default statusGraphqlService;